import { doc, setDoc, increment } from "firebase/firestore";
import { db } from "../firebaseConfig";

/**
 * Detect language of a query by Unicode script ranges.
 * Returns "fa" for Persian/Arabic, "zh" for Chinese, "en" for everything else.
 */
export function detectQueryLanguage(query: string): string {
  for (const ch of query) {
    const code = ch.codePointAt(0) || 0;
    // Arabic/Persian script range
    if (code >= 0x0600 && code <= 0x06FF) return "fa";
    // CJK Unified Ideographs
    if (code >= 0x4E00 && code <= 0x9FFF) return "zh";
  }
  return "en";
}

/**
 * Log a search query using counter pattern.
 * Instead of creating a new document per search, we increment a counter
 * in a daily document: searchCounts/{YYYY-MM-DD}
 *
 * Document structure:
 * {
 *   "crypto": 45,
 *   "news channels": 12,
 *   ...
 *   _total: 150,
 *   _date: "2026-02-19"
 * }
 *
 * This reduces Firestore writes from N (one per search) to N-unique-terms per day.
 * Multiple searches for the same term only trigger one field increment.
 */
export function logSearch(query: string, _userId?: string): void {
  const trimmed = query.trim().toLowerCase().slice(0, 100); // limit length
  if (!trimmed) return;

  const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
  const lang = detectQueryLanguage(trimmed);
  const ref = doc(db, "searchCounts", today);

  // Firestore field names can't contain '.', '/', or start with '__'
  const safeKey = trimmed.replace(/[./]/g, "_");
  // Prefix with language for filtering: "en:crypto", "fa:تلگرام"
  const langKey = `${lang}:${safeKey}`;

  // Fire and forget - merge to avoid overwriting other fields
  setDoc(ref, {
    [langKey]: increment(1),
    // Keep the old un-prefixed key for backwards compat during migration
    [safeKey]: increment(1),
    _total: increment(1),
    _date: today,
  }, { merge: true }).catch((err) =>
    console.warn("Failed to log search:", err)
  );
}
